import { useTranslation } from 'react-i18next';
import { Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PublicService } from '../types';

interface ServiceListProps {
  services: PublicService[];
  onBook: (service: PublicService) => void;
}

function GroupBadge() {
  const { t } = useTranslation('bookingPage');
  return (
    <span className="inline-flex h-6 items-center gap-1.5 rounded-pill bg-brand-50 px-2.5 text-[12px] font-semibold text-brand-700">
      <Users className="h-3 w-3" />
      {t('services.group')}
    </span>
  );
}

export function ServiceList({ services, onBook }: ServiceListProps) {
  const { t } = useTranslation('bookingPage');

  if (services.length === 0) {
    return <p className="rounded-2xl border border-line bg-surface p-5 text-body text-soft">{t('services.empty')}</p>;
  }

  return (
    <div className="flex flex-col gap-2.5 sm:gap-3" data-testid="service-list">
      {services.map((service) => {
        const isGroup = service.max_capacity > 1;
        return (
          <article
            key={service.id}
            className="flex items-center gap-4 rounded-2xl border border-line bg-surface p-4 sm:gap-6 sm:px-[22px] sm:py-5"
            data-testid="service-card"
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <h3 className="font-display text-[16px] font-semibold tracking-tight text-ink sm:text-[17px]">{service.name}</h3>
                {isGroup && <GroupBadge />}
              </div>
              {service.description && <p className="hidden text-body text-soft line-clamp-2 sm:block">{service.description}</p>}
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-body-sm text-soft">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {t('services.minutes', { count: service.duration_minutes })}
                </span>
                {!service.hide_price && <span className="tabular-nums sm:hidden">{service.formatted_price}</span>}
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2.5">
              {!service.hide_price && (
                <span className="hidden text-[18px] font-semibold tabular-nums text-ink sm:inline">{service.formatted_price}</span>
              )}
              <Button
                size="lg"
                className="h-11 px-5"
                onClick={() => onBook(service)}
                aria-label={t('services.bookService', { name: service.name })}
              >
                {t('services.book')}
              </Button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
